import { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { fmtKr } from "@/lib/tickets";
import { cn } from "@/lib/utils";

/* Mirrors backend ticketing.compute_fee so the numbers on this page are the numbers on the invoice. */
export const computeFee = (gross, qty, cfg, vat = 0) => {
  const model = cfg?.model || "none";
  let fee = 0;
  if (["percent", "percent_fixed"].includes(model)) fee += gross * (Number(cfg.percent) || 0) / 100;
  if (["fixed", "percent_fixed"].includes(model)) fee += (Number(cfg.fixed) || 0) * qty;
  if (qty && model !== "none") {
    let per = fee / qty;
    per = Math.max(per, Number(cfg.min_fee) || 0);
    if (cfg.max_fee !== null && cfg.max_fee !== undefined) per = Math.min(per, Number(cfg.max_fee));
    fee = per * qty;
  }
  const v = fee * (Number(vat) || 0) / 100;
  return { fee: Math.round(fee * 100) / 100, vat: Math.round(v * 100) / 100, total: Math.round((fee + v) * 100) / 100 };
};

const Big = ({ label, value, muted = false, accent = false, testId }) => (
  <div data-testid={testId}>
    <div className="t-mono text-muted-foreground">{label}</div>
    <div className={cn("mt-2 font-display text-[clamp(28px,3.4vw,44px)] font-semibold leading-none tracking-[-0.03em] tabular", muted && "text-muted-foreground", accent && "text-sol")}>{value}</div>
  </div>
);

/** «Kva sit du att med?» – price × audience × category → brutto, avgift, til deg. */
export const FeeCalculator = ({ tiers = [], vatPercent = 0, maxAudience = 187, className }) => {
  const [cat, setCat] = useState("external");
  const [price, setPrice] = useState(250);
  const [qty, setQty] = useState(120);
  const tier = tiers.find((t) => t.category === cat) || tiers[0];
  const gross = price * qty;
  const r = useMemo(() => computeFee(gross, qty, tier, vatPercent), [gross, qty, tier, vatPercent]);
  const net = Math.max(0, gross - r.total);
  const free = price === 0;
  return (
    <div className={cn("grid gap-10 lg:grid-cols-12 lg:gap-8", className)} data-testid="arranger-calculator">
      <div className="lg:col-span-6">
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Arrangørkategori">
          {tiers.map((t) => (
            <button key={t.category} type="button" role="radio" aria-checked={cat === t.category} onClick={() => setCat(t.category)} className={cn("inline-flex h-11 items-center gap-2 rounded-full px-4 text-[14px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2", cat === t.category ? "bg-foreground text-background" : "bg-secondary/80 hover:bg-secondary")} data-testid={`calc-cat-${t.category}`}>
              {t.label}<span className={cn("tabular text-[12px]", cat === t.category ? "text-background/70" : "text-muted-foreground")}>{t.fee_text}</span>
            </button>
          ))}
        </div>

        <div className="mt-8 space-y-7">
          <div>
            <div className="flex items-baseline justify-between gap-4">
              <label htmlFor="calc-price" className="text-[14px] font-medium">Pris per billett</label>
              <div className="flex items-center gap-2"><Input id="calc-price" type="number" inputMode="numeric" min="0" max="2000" step="10" value={price} onChange={(e) => setPrice(Math.max(0, Math.min(2000, Number(e.target.value) || 0)))} className="h-10 w-24 bg-card text-right tabular" data-testid="calc-price-input" /><span className="text-[14px] text-muted-foreground">kr</span></div>
            </div>
            <Slider value={[price]} min={0} max={600} step={10} onValueChange={([v]) => setPrice(v)} className="mt-4" aria-label="Pris per billett" data-testid="calc-price-slider" />
            <div className="mt-2 flex justify-between text-[12px] text-muted-foreground tabular"><span>Gratis</span><span>600 kr</span></div>
          </div>
          <div>
            <div className="flex items-baseline justify-between gap-4">
              <label htmlFor="calc-qty" className="text-[14px] font-medium">Selde billettar</label>
              <div className="flex items-center gap-2"><Input id="calc-qty" type="number" inputMode="numeric" min="1" max={maxAudience} value={qty} onChange={(e) => setQty(Math.max(1, Math.min(maxAudience, Number(e.target.value) || 1)))} className="h-10 w-24 bg-card text-right tabular" data-testid="calc-qty-input" /><span className="text-[14px] text-muted-foreground">stk</span></div>
            </div>
            <Slider value={[qty]} min={1} max={maxAudience} step={1} onValueChange={([v]) => setQty(v)} className="mt-4" aria-label="Selde billettar" data-testid="calc-qty-slider" />
            <div className="mt-2 flex justify-between text-[12px] text-muted-foreground tabular"><span>1</span><span>{maxAudience} (fullt amfi)</span></div>
          </div>
        </div>
      </div>

      <div className="lg:col-span-6">
        <div className="grid grid-cols-2 gap-x-6 gap-y-8 border-t border-border pt-8 sm:grid-cols-3" aria-live="polite">
          <Big label="Brutto billettsal" value={fmtKr(gross)} testId="calc-gross" />
          <Big label={`Avgift til huset${vatPercent ? " (inkl. mva)" : ""}`} value={free && tier?.model !== "fixed" && tier?.model !== "percent_fixed" ? "0 kr" : fmtKr(r.total)} muted testId="calc-fee" />
          <Big label="Til deg" value={fmtKr(net)} accent testId="calc-net" />
        </div>
        <ul className="mt-8 space-y-2 text-[14px] leading-relaxed text-muted-foreground">
          <li className="flex gap-3"><span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-foreground" aria-hidden="true" />Publikum betaler <span className="font-medium text-foreground">{fmtKr(gross)}</span> rett til deg – med Vipps eller i døra. Ingenting går via kulturhuset.</li>
          <li className="flex gap-3"><span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-foreground" aria-hidden="true" />Avgifta ({tier?.fee_text?.toLowerCase()}) vert fakturert i etterkant, berre av stadfesta sal.</li>
          <li className="flex gap-3"><span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-foreground" aria-hidden="true" />Romleige kjem i tillegg etter prislista. Kategori vert avklart med styret.</li>
        </ul>
      </div>
    </div>
  );
};
